import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import LegacyMobileDrawer from '../legacyMobileDrawer/LegacyMobileDrawer';
import axiosConn from '../../axiosConn';
import './legacyNav.css';

// Additional imports from Topbar
import { ViewPortContext } from '../../context/ViewPortContext';
import { useTheme, useThemeUpdate } from '../../context/ThemeContext';
import useMobileView from '../../hooks/useMobileView';
import fromToImg from '../../utils/fromToImg';
import { Search } from '@material-ui/icons';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import PlayerSearchResults from '../../components/playerSearchResults/PlayerSearchResults';
import { useGetCurrPlayer } from '../../hooks/useGetCurrPlayer';
import useClickOutside from '../../clickOutside';
import { useConversations } from '../../context/ConversationContext';
import { toast } from 'react-hot-toast';

// ✅ Your world-class NotificationsBell component
import NotificationsBell from '../notifications/NotificationsBell';
import JGLocalOpsMark from '../common/JGLocalOpsMark';
import {
  LOCALOPS_OPS_ROUTE,
  canViewLocalOpsOps,
} from '../../utils/localOpsOpsAccess';
import {
  DEFAULT_PROFILE_PHOTO_URL,
  getProfileCompletionPath,
  needsProfilePhotoCompletion,
} from '../../utils/profileCompletion';

const unlockGalaxyJGKAmount = 200;

const LegacyNav = ({}) => {
  // Auth and navigation
  const { player, logout } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  // Responsive and theme
  const { width } = useContext(ViewPortContext);
  const { darkTheme } = useTheme();
  const toggleTheme = useThemeUpdate();
  const mobileView = useMobileView();
  const from = localStorage.getItem('from');
  // Only magic-link-authenticated sessions should bypass "from" and go Home on logo tap.
  const isMagicLinkAuthed = player?.authMethod === 'magic_link';

  // State
  const [unreadCount, setUnreadCount] = useState(0); // kept for LegacyMobileDrawer prop
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currPlayer, setCurrPlayer] = useState({});
  const [unlockedGalaxies, setUnlockedGalaxies] = useState(false);
  const searchInput = useRef();
  const [searchedPlayers, setSearchedPlayers] = useState([]);
  const [playerSearchResults, setPlayerSearchResults] = useState(false);
  const menu = useRef(null);
  const [friendRequestPlayers, setFriendRequestPlayers] = useState([]);
  const [numFriendRequests, setNumFriendRequests] = useState(0);
  const [showFriendRequestPlayersMenu, setShowFriendRequestPlayersMenu] =
    useState(false);
  const friendRequestPlayersMenu = useRef(null);
  const {
    conversations,
    setConversations,
    totalUnReadMessages,
    setTotalUnReadMessages,
    conversationsStatus,
    conversationsData,
    refetchConversations,
  } = useConversations();

  const playermenu = useRef(null);
  const isLocalOpsActive =
    location?.pathname?.startsWith('/playermap') ||
    location?.pathname?.startsWith('/localops');
  const isOpsHealthActive = location?.pathname?.startsWith(LOCALOPS_OPS_ROUTE);
  const showOpsHealthEntry =
    canViewLocalOpsOps(player) &&
    (location?.pathname?.startsWith('/playermap') || isOpsHealthActive);
  const actionCreditCount =
    player?.data?.player?.actionCreditCount ??
    player?.data?.player?.actionCredit ??
    0;

  const handleClickOutside = () => {
    setPlayerSearchResults(false);
    searchInput.current.value = '';
  };

  useClickOutside(friendRequestPlayersMenu, () => {
    setShowFriendRequestPlayersMenu(false);
  });

  const Logoff = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const handleSearch = async (searchInputValue) => {
    if (searchInputValue === '') {
      setPlayerSearchResults(false);
      setSearchedPlayers([]);
    } else {
      setPlayerSearchResults(true);
      try {
        const res = await axiosConn.get(`players/regex/${searchInputValue}`, {
          headers: { Authorization: `Bearer ${player.token}` },
        });
        setSearchedPlayers(res.data.data.data);
      } catch (err) {
        setSearchedPlayers([]);
        console.log(err);
      }
    }
  };

  const profileImage =
    player?.data.player.profilePhoto ||
    DEFAULT_PROFILE_PHOTO_URL;
  const showProfilePhotoNudge = needsProfilePhotoCompletion(player);
  const profilePath = getProfileCompletionPath(player, {
    focus: showProfilePhotoNudge,
  });

  // Hamburger menu
  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  // Logout
  const handleLogout = () => {
    logout();
    history.push('/');
  };

  // Define callbacks for useGetCurrPlayer
  const onSuccess2 = (data) => {};
  const onError2 = (error) => {
    console.error(error);
  };

  const { data: data2, status: status2 } = useGetCurrPlayer(
    onSuccess2,
    onError2,
    player,
  );

  useEffect(() => {
    let mounted = true;
    if (status2 === 'success') {
      if (!mounted) return;
      setCurrPlayer(data2);
    }
    return () => {
      mounted = false;
    };
  }, [status2, data2]);

  useEffect(() => {
    let mounted = true;
    const getFriendRequestPlayers = async () => {
      try {
        const res1 = await axiosConn.get(
          `players/friendRequests/${player.data.player._id}`,
          { headers: { Authorization: `Bearer ${player.token}` } },
        );
        if (!mounted) return;
        setFriendRequestPlayers(res1.data);
        setNumFriendRequests(res1.data.length);
      } catch (err) {
        console.log(err);
      }
    };
    getFriendRequestPlayers();
    return () => {
      mounted = false;
    };
  }, [player?.data.player._id, player?.token]);

  // Fetch unread notifications for LegacyMobileDrawer prop
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const { data } = await axiosConn.get('notifications/my/unread-count', {
          headers: { Authorization: `Bearer ${player?.token}` },
        });
        setUnreadCount(data?.data?.count || data?.count || 0);
      } catch (err) {
        console.error('Failed to fetch unread notifications');
      }
    };
    if (player?.token) fetchUnreadCount();
  }, [player?.token]);

  // Fetch current player (for unlocking galaxies, etc.)
  useEffect(() => {
    let mounted = true;
    const fetchCurrPlayer = async () => {
      try {
        const { data } = await axiosConn.get(
          `players/${player.data.player._id}`,
          { headers: { Authorization: `Bearer ${player?.token}` } },
        );
        if (!mounted) return;
        setCurrPlayer(data.data.data);
      } catch (err) {
        console.error('Failed to fetch current player');
      }
    };
    if (player?.token) fetchCurrPlayer();
    return () => {
      mounted = false;
    };
  }, [player?.token]);

  // Unlock galaxies logic
  useEffect(() => {
    const calculateAge = (bDay, bMonth, bYear) => {
      if (typeof bDay !== 'number' || bDay < 1 || bDay > 31) return null;
      if (typeof bMonth !== 'number' || bMonth < 1 || bMonth > 12) return null;
      if (
        typeof bYear !== 'number' ||
        bYear < 1900 ||
        bYear > new Date().getFullYear()
      )
        return null;
      const today = new Date();
      const birthDate = new Date(bYear, bMonth - 1, bDay);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age;
    };

    if (currPlayer?.jgkTokens >= unlockGalaxyJGKAmount) {
      const age = calculateAge(
        currPlayer?.bDay,
        currPlayer?.bMonth,
        currPlayer?.bYear,
      );
      if (age >= 18 || age === null) {
        localStorage.setItem('unlockedGalaxies', 'true');
        setUnlockedGalaxies(true);
      } else {
        localStorage.setItem('unlockedGalaxies', 'false');
        setUnlockedGalaxies(false);
      }
    } else {
      localStorage.setItem('unlockedGalaxies', 'false');
    }
  }, [currPlayer]);

  useEffect(() => {
    // console.log('LegacyNav conversationsData: ', conversationsData);
    let mounted = true;
    if (conversationsStatus === 'success') {
      if (!mounted) return;
      setConversations(conversationsData);
    }
    return () => {
      mounted = false;
    };
  }, [conversationsStatus, conversationsData, setConversations]);

  useEffect(() => {
    if (typeof setTotalUnReadMessages !== 'function') return;
    let mounted = true;
    let total = 0;
    conversations?.forEach((conversation) => {
      total += conversation.unReadMessages ?? 0;
    });
    if (!mounted) return;
    setTotalUnReadMessages(total);
    return () => {
      setTotalUnReadMessages(0);
      mounted = false;
    };
  }, [conversations]);

  useEffect(() => {
    const handleFocus = () => {
      if (typeof refetchConversations === 'function') {
        refetchConversations();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchConversations]);

  return (
    <>
      <nav className="legacy-nav" ref={friendRequestPlayersMenu}>
        <div className="nav-container">
          {/* Mobile view: logo + bell + hamburger */}
          {mobileView ? (
            <>
              <div className="nav-mobile-row">
                <Link
                  to={
                    isMagicLinkAuthed
                      ? '/home'
                      : unlockedGalaxies ||
                        player.data.player.role === 'admin' ||
                        player.data.player.role === 'moderator' ||
                        player.data.player.role === 'galaxyAdmin'
                      ? '/home'
                      : from && from !== 'null'
                      ? from
                      : '/'
                  }
                  className="nav-brand nav-slot-left"
                >
                  <img
                    src={
                      'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png'
                    }
                    alt="JG Logo"
                    className="topbarJGLogoImgMobile"
                  />
                </Link>

                <div className="nav-slot-center">
                  <NotificationsBell className="topbar-bell" />
                </div>

                <button
                  className="menu-toggle nav-slot-right"
                  onClick={toggleDrawer}
                  aria-label="Open menu"
                >
                  ☰
                  {(totalUnReadMessages > 0 ||
                    numFriendRequests > 0 ||
                    unreadCount > 0) && (
                    <span className="hamburger-badges">
                      {totalUnReadMessages > 0 && (
                        <span className="badge badge-messages" title="Messages">
                          💬 {totalUnReadMessages}
                        </span>
                      )}
                      {numFriendRequests > 0 && (
                        <span className="badge badge-requests" title="Requests">
                          🤝 {numFriendRequests}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Desktop view: logo, search, nav links */}
              <Link
                to={
                  isMagicLinkAuthed
                    ? '/home'
                    : unlockedGalaxies ||
                      player?.data.player.role === 'admin' ||
                      player?.data.player.role === 'moderator' ||
                      player?.data.player.role === 'galaxyAdmin'
                    ? '/home'
                    : from && from !== 'null'
                    ? from
                    : '/'
                }
                className="nav-brand"
              >
                {width < 777 ? (
                  <img
                    src={
                      'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png'
                    }
                    alt=""
                    className="topbarJGLogoImg"
                  />
                ) : (
                  <img
                    src={fromToImg(from, 'Topbar')}
                    alt=""
                    className="topbarJGLogoImg"
                  />
                )}
              </Link>
              <Link
                to="/playermap"
                className={`nav-mode-entry${isLocalOpsActive ? ' active' : ''}`}
              >
                <JGLocalOpsMark actionCreditCount={actionCreditCount} />
              </Link>

              <div className="topbarCenter">
                <div className="searchbar">
                  <Search className="searchIcon" />
                  <label className="labelSearch" htmlFor="search">
                    <input
                      type="search"
                      autoComplete="on"
                      aria-label="Search"
                      placeholder={
                        width > 466 ? 'Search for players' : 'Search'
                      }
                      className="searchInput"
                      onChange={(e) => handleSearch(e.target.value)}
                      ref={searchInput}
                    />
                  </label>
                  {playerSearchResults && searchedPlayers.length > 0 && (
                    <div className="playerSearchResults" ref={menu}>
                      <PlayerSearchResults searchedPlayers={searchedPlayers} />
                    </div>
                  )}
                </div>
              </div>

              <button
                className="menu-toggle"
                onClick={toggleDrawer}
                aria-label="Open menu"
              >
                ☰
              </button>

              <div className="nav-right desktop-only">
                <Link
                  to={profilePath}
                  className={`nav-profile-link${
                    showProfilePhotoNudge ? ' nav-profile-link--needs-photo' : ''
                  }`}
                  aria-label={
                    showProfilePhotoNudge
                      ? 'Profile - add photo to earn JGK'
                      : 'Profile'
                  }
                >
                  <span className="nav-profile-main">
                    <img src={profileImage} alt="avatar" className="nav-avatar" />
                    Profile
                  </span>
                  {showProfilePhotoNudge ? (
                    <span
                      className="nav-profile-nudge"
                      data-testid="profile-photo-nav-nudge"
                    >
                      Add photo <strong>+4 JGK</strong>
                    </span>
                  ) : null}
                </Link>

                <Link
                  to="/messenger/''"
                  style={{
                    textDecoration: 'none',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  💬 Messages
                  {totalUnReadMessages > 0 && (
                    <span
                      className="topbarIconBadge"
                      style={{
                        position: 'absolute',
                        top: -2,
                        right: -8,
                        background: '#facc15',
                        color: '#222',
                        borderRadius: '50%',
                        padding: '2px 7px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        zIndex: 1,
                      }}
                    >
                      {totalUnReadMessages}
                    </span>
                  )}
                </Link>

                <div
                  style={{
                    textDecoration: 'none',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginRight: '1rem',
                    minWidth: '90px',
                  }}
                  onClick={() =>
                    setShowFriendRequestPlayersMenu((prev) => !prev)
                  }
                >
                  🤝 Requests
                  {numFriendRequests > 0 && (
                    <span
                      className="topbarIconBadge"
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -18,
                        background: '#facc15',
                        color: '#222',
                        borderRadius: '50%',
                        padding: '2px 7px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        zIndex: 1,
                      }}
                    >
                      {numFriendRequests}
                    </span>
                  )}
                </div>

                <Link to="/jgktoken">
                  💰 {parseFloat(currPlayer?.jgkTokens || 0).toFixed(2)} JGK
                </Link>

                {true && (
                  <Link to="/presence-directory">Presence Directory</Link>
                )}

                {showOpsHealthEntry && (
                  <Link
                    to={LOCALOPS_OPS_ROUTE}
                    className={`ops-health-nav-link${
                      isOpsHealthActive ? ' active' : ''
                    }`}
                  >
                    <HealthAndSafetyOutlinedIcon fontSize="small" />
                    Ops Health
                  </Link>
                )}

                {/* ✅ Replace old "🔔 Notifications" link with the live bell */}
                <NotificationsBell className="topbar-bell" />

                <button
                  onClick={() => {
                    if (window.confirm('Ready to blaze out? 🚪')) {
                      Logoff();
                    }
                  }}
                >
                  🚪 Logout
                </button>
              </div>

              {showFriendRequestPlayersMenu && (
                <div className="friendRequestPlayersSearchResults">
                  <PlayerSearchResults
                    searchedPlayers={friendRequestPlayers}
                    onPlayerClick={() => setShowFriendRequestPlayersMenu(false)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </nav>

      <LegacyMobileDrawer
        show={drawerOpen}
        onClose={toggleDrawer}
        player={player}
        unreadCount={unreadCount}
        friendRequestPlayers={friendRequestPlayers}
      />
    </>
  );
};

export default LegacyNav;
